import isEqual from 'lodash/isEqual';
import round from 'lodash/round';

import {
  EllipsisWithTooltip,
  ExternalCell,
  ExternalRow,
  Icons,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { WorkloadOptimizationState } from '@cast/types';
import { convertUnit } from '@cast/utils';

import { DetailsRowContainer, DetailWitTitle } from './DetailsRowContainer';
import { HighlightedItem } from './HighlightedItem';
import { OptimizationStateItem } from './OptimizationStateItem';
import { PodCountItem } from './PodCountItem';
import { ConfigChangedEventV2 } from '../../../types';

const toGibOrNull = (val?: number | null) =>
  val ? convertUnit(val, 'MiB', 'GiB') : null;

const ResourceComparisonItem = ({
  prev,
  curr,
  unit,
}: {
  prev?: number | null;
  curr?: number | null;
  unit: string;
}) => {
  if (prev === curr) {
    return null;
  }
  return (
    <Stack direction="row" alignItems="center" gap={8}>
      <Typography variant="P12R" minWidth={32}>
        {prev ?? '-'}
      </Typography>
      <Icons.ArrowRight
        fontSize="16px"
        css={({ palette }) => ({ color: palette.grey[400] })}
      />
      <Typography variant="P12R">{curr ?? '-'}</Typography>
      {unit && (
        <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
          {unit}
        </Typography>
      )}
    </Stack>
  );
};

type RowProps = {
  event: ConfigChangedEventV2;
};

const HPARow = ({ event }: RowProps) => {
  const { previous, current } = event.details;

  const prevConfig = previous.workloadConfig.hpaConfig;
  const currConfig = current.workloadConfig.hpaConfig;

  if (isEqual(prevConfig, currConfig)) {
    return null;
  }

  return (
    <ExternalRow height={104} py={12} sx={{ alignItems: 'flex-start' }}>
      <ExternalCell startColumn={0}>
        <DetailWitTitle title="Horizontal rightsizing:" />
      </ExternalCell>
      <ExternalCell startColumn={1}>
        <Stack gap={12}>
          <OptimizationStateItem
            prev={
              prevConfig.managementOption === WorkloadOptimizationState.MANAGED
            }
            curr={
              currConfig.managementOption === WorkloadOptimizationState.MANAGED
            }
            label="Automatic Optimization:"
          />
          <Stack gap={4}>
            <Typography variant="L10M">POD COUNT:</Typography>
            <Stack direction="row" alignItems="center" gap={40}>
              <PodCountItem
                prev={prevConfig.minReplicas}
                curr={currConfig.minReplicas}
                label="Min"
              />

              <PodCountItem
                prev={prevConfig.maxReplicas}
                curr={currConfig.maxReplicas}
                label="Max"
              />
            </Stack>
          </Stack>
        </Stack>
      </ExternalCell>
    </ExternalRow>
  );
};

const VPARow = ({ event }: RowProps) => {
  const { previous, current } = event.details;

  const prevConfig = previous.workloadConfig.vpaConfig;
  const currConfig = current.workloadConfig.vpaConfig;

  if (
    isEqual(prevConfig, currConfig) &&
    isEqual(previous.scalingPolicyConfig, current.scalingPolicyConfig)
  ) {
    return null;
  }

  const stateChanged =
    prevConfig.managementOption !== currConfig.managementOption;

  const baseHeight = 44;
  const height = baseHeight + (stateChanged ? 28 : 0);

  return (
    <ExternalRow height={height} py={12} sx={{ alignItems: 'flex-start' }}>
      <ExternalCell startColumn={0}>
        <DetailWitTitle title="Vertical rightsizing:" />
      </ExternalCell>
      <ExternalCell startColumn={1} cellProps={{ width: '100%' }}>
        <Stack gap={12} width="100%">
          <Stack direction="row" gap={8} alignItems="center">
            <Typography variant="L10M">Vertical Scaling Policy:</Typography>
            <Typography variant="P10R" color="blue.500">
              {event.details.current.scalingPolicyConfig.name}
            </Typography>
          </Stack>
          {stateChanged && (
            <OptimizationStateItem
              prev={
                prevConfig.managementOption ===
                WorkloadOptimizationState.MANAGED
              }
              curr={
                currConfig.managementOption ===
                WorkloadOptimizationState.MANAGED
              }
              label="Automatic Optimization:"
            />
          )}
        </Stack>
      </ExternalCell>
    </ExternalRow>
  );
};

const ContainerChangesRows = ({ event }: RowProps) => {
  const rows = event.containerNames.map((name) => {
    const prev =
      event.details.previous.workloadConfig.vpaConfig.containerConstraints?.find(
        (c) => c.containerName === name
      );
    const curr =
      event.details.current.workloadConfig.vpaConfig.containerConstraints?.find(
        (c) => c.containerName === name
      );

    const numOrNothing = (num?: number | null) =>
      num != null ? round(num, 3) : undefined;

    const constraintVal = (
      resource:
        | {
            constraints?: {
              min?: {
                constant?: { value?: number };
                percentageOfOriginal?: { value?: number };
              };
              max?: {
                constant?: { value?: number };
                percentageOfOriginal?: { value?: number };
              };
            };
          }
        | undefined,
      key: 'min' | 'max'
    ) => {
      const c = resource?.constraints?.[key];
      return numOrNothing(c?.constant?.value ?? c?.percentageOfOriginal?.value);
    };

    return {
      name,
      cpu: {
        prev: {
          min: constraintVal(prev?.cpu, 'min'),
          max: constraintVal(prev?.cpu, 'max'),
        },
        curr: {
          min: constraintVal(curr?.cpu, 'min'),
          max: constraintVal(curr?.cpu, 'max'),
        },
      },
      memory: {
        prev: {
          min: toGibOrNull(constraintVal(prev?.memory, 'min')),
          max: toGibOrNull(constraintVal(prev?.memory, 'max')),
        },
        curr: {
          min: toGibOrNull(constraintVal(curr?.memory, 'min')),
          max: toGibOrNull(constraintVal(curr?.memory, 'max')),
        },
      },
    };
  });

  if (!rows.length) {
    return null;
  }

  return (
    <>
      <ExternalRow typography="L10B" height={36}>
        <ExternalCell>Container</ExternalCell>
        <ExternalCell startColumn={1} cellProps={{ width: '100%' }}>
          <Box
            display="grid"
            gridTemplateColumns="repeat(4,1fr)"
            width="100%"
            rowGap={16}
          >
            <Typography variant="L10B">CPU MIN</Typography>
            <Typography variant="L10B">CPU MAX</Typography>
            <Typography variant="L10B" textTransform="initial">
              GiB MIN
            </Typography>
            <Typography variant="L10B" textTransform="initial">
              GiB MAX
            </Typography>
          </Box>
        </ExternalCell>
      </ExternalRow>
      {rows.map(({ name, cpu, memory }) => (
        <ExternalRow key={name} typography="P12R" height={28}>
          <ExternalCell>
            <EllipsisWithTooltip
              TypographyProps={{
                pr: 16,
                variant: 'inherit',
              }}
            >
              {name}
            </EllipsisWithTooltip>
          </ExternalCell>
          <ExternalCell startColumn={1} cellProps={{ width: '100%' }}>
            <Box
              display="grid"
              gridTemplateColumns="repeat(4,1fr)"
              width="100%"
              rowGap={16}
            >
              <ResourceComparisonItem
                prev={cpu.prev.min}
                curr={cpu.curr.min}
                unit="CPU"
              />
              <ResourceComparisonItem
                prev={cpu.prev.max}
                curr={cpu.curr.max}
                unit="CPU"
              />
              <ResourceComparisonItem
                prev={memory.prev.min}
                curr={memory.curr.min}
                unit="GiB"
              />
              <ResourceComparisonItem
                prev={memory.prev.max}
                curr={memory.curr.max}
                unit="GiB"
              />
            </Box>
          </ExternalCell>
        </ExternalRow>
      ))}
    </>
  );
};

const ContainerRows = ({ event }: RowProps) => {
  const prevContainerByName = Object.fromEntries(
    event.details.previous.workloadConfig.vpaConfig.containerConstraints?.map(
      (c) => [c.containerName, c]
    ) ?? []
  );

  const modifiedCount =
    event.details.current.workloadConfig.vpaConfig.containerConstraints.filter(
      (c) => {
        const prev = prevContainerByName[c.containerName];
        return !(prev && isEqual(c, prev));
      }
    ).length;

  const totalCount =
    event.details.current.workloadConfig.vpaConfig.containerConstraints?.length;

  if (!modifiedCount) {
    return null;
  }

  return (
    <>
      <ExternalRow height={82} py={12} sx={{ alignItems: 'flex-start' }}>
        <ExternalCell startColumn={0}>
          <DetailWitTitle title="Container constraints:" />
        </ExternalCell>
        <ExternalCell startColumn={1}>
          <Stack gap={4}>
            <Typography variant="L10M">Containers</Typography>
            <Stack direction="row" alignItems="center" gap={40}>
              <HighlightedItem label="Modified">
                <Typography variant="P10R">{modifiedCount}</Typography>
              </HighlightedItem>
              <HighlightedItem label="Total">
                <Typography variant="P10R">{totalCount ?? '-'}</Typography>
              </HighlightedItem>
            </Stack>
          </Stack>
        </ExternalCell>
      </ExternalRow>
      <ContainerChangesRows event={event} />
    </>
  );
};

export const ConfigurationEventSubRow = ({ event }: RowProps) => {
  return (
    <DetailsRowContainer event={event}>
      {event.initiatedBy?.email && (
        <ExternalRow
          height={22}
          top={-12}
          sx={{ borderTopColor: 'transparent !important' }}
        >
          <ExternalCell startColumn={0}>
            <DetailWitTitle
              title="Initiated by:"
              detail={event.initiatedBy.email}
              titleVariant="light"
            />
          </ExternalCell>
        </ExternalRow>
      )}
      <HPARow event={event} />
      <VPARow event={event} />
      <ContainerRows event={event} />
    </DetailsRowContainer>
  );
};
